import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Lock, ArrowRight, Eye, EyeOff, Loader2, Video, Users, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const isLocked = lockedUntil !== null && Date.now() < lockedUntil;
  const MAX_ATTEMPTS = 5;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isLocked) {
      toast({
        title: "Muitas tentativas",
        description: "Aguarde 1 minuto antes de tentar novamente.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    const { error } = await signIn(email, password);

    if (error) {
      setIsLoading(false);
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);

      if (newAttempts >= MAX_ATTEMPTS) {
        setLockedUntil(Date.now() + 60_000);
        setTimeout(() => { setLockedUntil(null); setLoginAttempts(0); }, 60_000);
        toast({
          title: "Conta temporariamente bloqueada",
          description: "Muitas tentativas. Aguarde 1 minuto.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Ops, deu ruim",
        description: error.message === "Invalid login credentials"
          ? `Email ou senha incorretos. (${MAX_ATTEMPTS - newAttempts} tentativas restantes)`
          : error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Bora! 🎉",
      description: "Bem-vindo de volta ao Publee!",
    });

    sessionStorage.setItem('just_logged_in', 'true');
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="flex items-center gap-2 mb-12">
            <span className="text-2xl font-bold text-foreground tracking-tight">
              Pub<span className="text-gradient">lee</span>
            </span>
          </Link>

          <h1 className="text-3xl font-bold mb-2">E aí, voltou!</h1>
          <p className="text-muted-foreground mb-8">
            Suas publis estão te esperando. Bora criar mais?
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium mb-2"
              >
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-12 bg-secondary border-border"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium mb-2"
              >
                Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 pr-12 h-12 bg-secondary border-border"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Link
                to="/recuperar-senha"
                className="text-sm text-primary hover:underline"
              >
                Esqueceu a senha?
              </Link>
            </div>

            <Button
              type="submit"
              variant="hero"
              size="lg"
              className="w-full"
              disabled={isLoading || isLocked}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Entrando...
                </>
              ) : (
                <>
                  Entrar
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </form>

          <p className="text-center text-muted-foreground mt-8">
            Ainda não tem conta?{" "}
            <Link to="/cadastro" className="text-primary hover:underline">
              Cria uma grátis
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Right Panel - Visual */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <div className="absolute inset-0 gradient-void" />
        <div className="absolute inset-0 gradient-mist-gold" />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-8 p-12">
          {/* Demo video */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="aspect-[9/16] w-[200px] rounded-2xl gradient-card-depth border border-border overflow-hidden shadow-z3">
              <video
                src="/video_demo.mp4"
                poster="/imagem_demo.png"
                className="w-full h-full object-cover"
                playsInline
                preload="metadata"
                muted
                loop
                autoPlay
              />
            </div>
            <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-gold-primary/5 blur-2xl pointer-events-none" />
            <div className="absolute -bottom-4 -left-4 w-24 h-24 rounded-full bg-gold-primary/5 blur-2xl pointer-events-none" />
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex items-center gap-8"
          >
            {[
              { icon: Video, value: "3", label: "vídeos/crédito" },
              { icon: Clock, value: "~5min", label: "por publi" },
              { icon: Users, value: "72+", label: "criadores" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-lg font-bold text-gradient">{stat.value}</div>
                <div className="text-[10px] text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
